import { useCallback, useRef, useEffect } from 'react';
import { format } from 'date-fns/format';
import { toZonedTime } from 'date-fns-tz';
import { differenceInDays } from 'date-fns/differenceInDays';
import {
  PaymentInitiatorEnum,
  PaymentStatusEnum,
  WorkingModeEnum,
} from 'src/models';

export const MOSCOW_TZ = 'Europe/Moscow';

export const goToNextElem = (tabIndex?: number) => {
  if (tabIndex === undefined || [-1, 0].includes(tabIndex)) {
    return;
  }

  setTimeout(() => {
    try {
      const elements = [
        ...document.querySelectorAll(
          'button:not([disabled]), input[type=text]:not([disabled]), textarea:not([disabled]), [tabindex]:not([disabled]):not([tabindex="-1"]):not([tabindex="0"])',
        ),
      ].filter((elem) => elem.getClientRects().length > 0);

      const nextElem = elements.find(
        (elem) => (elem as HTMLElement).tabIndex === tabIndex + 1,
      );

      if (nextElem) {
        (nextElem as HTMLElement).focus?.();
      }
    } catch (e) {
      console.error('goToNextElem error', e);
    }
  }, 50);
};

export const uuidV4 = () => {
  return '10000000-1000-4000-8000-100000000000'.replace(/[018]/g, (c) =>
    (
      +c ^
      (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (+c / 4)))
    ).toString(16),
  );
};

export const formatDatetime = (date: string): string => {
  if (!date) {
    return '-';
  }
  return format(new Date(date), 'dd.MM HH:mm:ss');
};

export const formatDate = (date?: string): string => {
  if (!date) {
    return '-';
  }
  return format(new Date(date), 'dd.MM.yyyy');
};

export const formatShortDate = (date?: string): string => {
  if (!date) {
    return '-';
  }
  return format(new Date(date), 'dd.MM.yy HH:mm');
};

export const formatMskShortDate = (date?: string): string => {
  if (!date) {
    return '-';
  }
  return format(toZonedTime(new Date(date), MOSCOW_TZ), 'dd.MM.yy HH:mm');
};

export const formatTime = (date?: string): string => {
  if (!date) {
    return '-';
  }
  return format(new Date(date), 'HH:mm');
};

export const isWeekend = (date: string) => {
  const day = new Date(date).getDay();
  return day === 6 || day === 0;
};

export const daysFromToday = (date: string): number | '' => {
  return date && differenceInDays(new Date(), new Date(date));
};

export const getLastUsedColor = (date: string): string => {
  const days = daysFromToday(date);
  if (days > 7) return 'pink';
  if (days > 5) return 'warning';
  return '';
};

export const getLastActivityColor = (date: string): string => {
  const days = daysFromToday(date);
  if (days > 5) return 'pink';
  if (days > 3) return 'warning';
  return 'gray';
};

export const currencySign = (currency?: string): string => {
  if (!currency) {
    return '₽';
  }

  switch (currency.toLowerCase()) {
    case 'rub':
      return '₽';
    default:
      return currency || '';
  }
};

export const formatDuration = (duration: number): string => {
  if (!duration) {
    return '-';
  }

  const hours = duration / 60;
  const rHours = Math.floor(hours);
  const minutes = (hours - rHours) * 60;
  const rMinutes = Math.round(minutes);

  return `${rHours.toLocaleString(undefined, { minimumIntegerDigits: 2 })}:${rMinutes.toLocaleString(
    undefined,
    {
      minimumIntegerDigits: 2,
    },
  )}`;
};

export const formatWorkingMode = (data: string): string => {
  switch (data) {
    case WorkingModeEnum.inRoom:
      return 'workingMode.inRoom';
    case WorkingModeEnum.reception:
      return 'workingMode.reception';
    case WorkingModeEnum.vipAccess:
      return 'workingMode.vipAccess';
    default:
      return 'workingMode.unknown';
  }
};

export const formatPaymentInitiator = (data: string): string => {
  switch (data) {
    case PaymentInitiatorEnum.headset:
      return 'paymentInitiator.headset';
    case PaymentInitiatorEnum.desk:
      return 'paymentInitiator.desk';
    case PaymentInitiatorEnum.qr_code:
      return 'paymentInitiator.qrCode';
    default:
      return 'paymentInitiator.notSelected';
  }
};

export const formatPaymentStatus = (data: string): string => {
  switch (data) {
    case PaymentStatusEnum.succeeded:
      return 'paymentStatus.succeeded';
    case PaymentStatusEnum.pending:
      return 'paymentStatus.pending';
    case PaymentStatusEnum.canceled:
      return 'paymentStatus.canceled';
    default:
      return 'paymentStatus.unknown';
  }
};

export const compareString = (
  a: string | null | undefined,
  b: string | null | undefined,
) => {
  const cmp = a || '-';
  return cmp.localeCompare(b || '-');
};

export const hybridCompare = (
  a: string | null | undefined,
  b: string | null | undefined,
) => {
  const leftParts = a?.match(/\d+/g) ?? null;
  const rightParts = b?.match(/\d+/g) ?? null;

  if (leftParts === null && rightParts === null) {
    return compareString(a, b);
  }

  if (leftParts !== null && rightParts !== null) {
    return Number(leftParts[0]) - Number(rightParts[0]);
  }

  return leftParts === null ? 1 : -1;
};

export const formatDurationHoursMinutes = (
  duration: number,
): [string, string] => {
  if (!duration) {
    return ['-', '-'];
  }

  const hours = duration / 60;
  const rHours = Math.floor(hours);
  const minutes = (hours - rHours) * 60;
  const rMinutes = Math.round(minutes);

  return [
    rHours.toLocaleString(undefined, { minimumIntegerDigits: 1 }),
    rMinutes.toLocaleString(undefined, { minimumIntegerDigits: 2 }),
  ];
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type CallbackFunction<T extends any[]> = (...args: T) => void;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const useDebounce = <T extends any[]>(
  callback: CallbackFunction<T>,
  delay: number,
): CallbackFunction<T> => {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Очищаем таймаут при размонтировании
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return useCallback(
    (...args: T) => {
      // Очищаем предыдущий таймаут
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Устанавливаем новый таймаут
      timeoutRef.current = setTimeout(() => {
        callback(...args);
      }, delay);
    },
    [callback, delay],
  );
};

export const checkIsLaptop = () =>
  window.innerWidth <= 960 || window.innerHeight <= 640;

function canAccessLocalStorage(): boolean {
  try {
    const key = '__LOCAL_STORAGE_TEST__';

    window.localStorage.setItem(key, key);
    window.localStorage.removeItem(key);

    return true;
  } catch {
    return false;
  }
}

export function getLocalStorageItem<T>(key: string, fallbackValue: T): T {
  if (!canAccessLocalStorage()) {
    return fallbackValue;
  }

  const rawValue = window.localStorage.getItem(key);

  if (rawValue === null) {
    return fallbackValue;
  }

  try {
    return JSON.parse(rawValue);
  } catch {
    return fallbackValue;
  }
}

export function setLocalStorageItem<T>(key: string, value: T): boolean {
  if (!canAccessLocalStorage()) {
    return false;
  }

  try {
    window.localStorage.setItem(key, JSON.stringify(value));

    return true;
  } catch {
    return false;
  }
}
